import { computed, onMounted, onUnmounted, ref } from "vue";
import type { ComputedRef, Ref } from "vue";
import { useTranslations } from "@/composables/useTranslations";

interface IChatterOptions {
    firstDelay?: number;
    visibleMs?: number;
    gapMs?: number;
}

interface IChatter {
    visible: Ref<boolean>;
    phrase: Ref<string>;
    poke: () => void;
}

/**
 * Cycles a mascot's speech bubble: shows a phrase for a while, hides it, waits,
 * then shows the next one. Phrases rotate in order so they never repeat back to
 * back. `poke()` jumps straight to the next phrase (used when the robot is
 * clicked). Timers are cleaned up on unmount.
 */
export function useRobotChatter(
    phrases: () => string[],
    options: IChatterOptions = {},
): IChatter {
    const firstDelay = options.firstDelay ?? 800;
    const visibleMs = options.visibleMs ?? 4200;
    const gapMs = options.gapMs ?? 9000;

    const visible = ref(false);
    const phrase = ref("");
    let index = 0;
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    const clearTimers = (): void => {
        window.clearTimeout(showTimer);
        window.clearTimeout(hideTimer);
    };

    const showNext = (): void => {
        const list = phrases();
        if (list.length === 0) {
            cycle(gapMs);
            return;
        }
        phrase.value = list[index % list.length];
        index += 1;
        visible.value = true;
        hideTimer = window.setTimeout(() => {
            visible.value = false;
            cycle(gapMs);
        }, visibleMs);
    };

    const cycle = (delay: number): void => {
        showTimer = window.setTimeout(showNext, delay);
    };

    const poke = (): void => {
        clearTimers();
        showNext();
    };

    onMounted(() => cycle(firstDelay));
    onUnmounted(clearTimers);

    return { visible, phrase, poke };
}

/**
 * The DMARCeye assistant's short chat phrases, shared by every mascot in the
 * full-page AI chat so the robot "talks" consistently.
 */
export function useRobotPhrases(): ComputedRef<string[]> {
    const { t } = useTranslations();

    return computed(() => [
        t("vue.ai_chat_welcome_wave"),
        t("vue.ai_chat_robot_help"),
        t("vue.ai_chat_robot_ask"),
        t("vue.ai_chat_robot_here"),
    ]);
}
